// DataMeridian AI CRM — mounted at /admin/*. Private; Clerk email+password gate.
import { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { SignIn, SignOutButton, useAuth } from "@clerk/react";
import { Toaster as AdminToaster } from "./components/ui/sonner";
import ClerkAuthProvider from "./components/auth/ClerkAuthProvider";
import Layout from "./components/Layout";
import { MeContext, useApi } from "./lib/api";
import Overview from "./pages/Overview";
import Discover from "./pages/Discover";
import Companies from "./pages/Companies";
import CompanyDetail from "./pages/CompanyDetail";
import Opportunities from "./pages/Opportunities";
import OpportunityDetail from "./pages/OpportunityDetail";
import Contacts from "./pages/Contacts";
import Outreach from "./pages/Outreach";
import Inbox from "./pages/Inbox";
import Followups from "./pages/Followups";
import Proposals from "./pages/Proposals";
import RadarPage from "./pages/Radar";
import Skills from "./pages/Skills";
import Integrations from "./pages/Integrations";
import Reports from "./pages/Reports";
import SettingsPage from "./pages/Settings";

function AuthShell({ children }) {
  return (
    <div className="flex min-h-screen">
      <div className="meridian-grid relative hidden w-1/2 flex-col justify-between bg-[#0F172A] p-12 text-white lg:flex">
        <div className="font-display text-sm font-extrabold tracking-[0.2em]">DATAMERIDIAN AI</div>
        <div>
          <h1 className="font-display text-4xl font-bold leading-tight xl:text-5xl">Client acquisition &<br />lead intelligence.</h1>
          <p className="mt-4 max-w-md text-slate-400">Evidence-based discovery of international database consulting opportunities, with automatic, opt-out-safe outreach.</p>
        </div>
        <div className="text-xs text-slate-500">Internal application · DataMeridian Consulting</div>
      </div>
      <div className="flex flex-1 items-center justify-center p-6">{children}</div>
    </div>
  );
}

function Protected() {
  const { isLoaded, isSignedIn } = useAuth();
  const api = useApi();
  const [me, setMe] = useState(null);
  const [denied, setDenied] = useState(false);
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    api.get("/me").then(setMe).catch(() => setDenied(true));
  }, [api, isLoaded, isSignedIn]);
  if (isLoaded && !isSignedIn) return <Navigate to="/admin/sign-in" replace />;
  if (denied) return (
    <AuthShell>
      <div className="max-w-sm text-center" data-testid="admin-access-denied">
        <h2 className="font-display text-xl font-bold text-slate-900">Restricted access</h2>
        <p className="mt-2 text-sm text-slate-500">This portal is private and limited to the DataMeridian owner account.</p>
        <SignOutButton>
          <button data-testid="admin-denied-sign-out" className="mt-6 rounded-md bg-[#0F172A] px-4 py-2 text-sm text-white hover:bg-slate-700 transition-colors">Sign out</button>
        </SignOutButton>
      </div>
    </AuthShell>
  );
  if (!me) return <div className="p-10 text-sm text-slate-500">Loading…</div>;
  return (
    <MeContext.Provider value={{ me, isAdmin: me.role === "admin" }}>
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<Overview />} />
          <Route path="discover" element={<Discover />} />
          <Route path="companies" element={<Companies />} />
          <Route path="companies/:id" element={<CompanyDetail />} />
          <Route path="opportunities" element={<Opportunities />} />
          <Route path="opportunities/:id" element={<OpportunityDetail />} />
          <Route path="contacts" element={<Contacts />} />
          <Route path="outreach" element={<Outreach />} />
          <Route path="inbox" element={<Inbox />} />
          <Route path="followups" element={<Followups />} />
          <Route path="proposals" element={<Proposals />} />
          <Route path="radar" element={<RadarPage />} />
          <Route path="skills" element={<Skills />} />
          <Route path="integrations" element={<Integrations />} />
          <Route path="reports" element={<Reports />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Route>
      </Routes>
    </MeContext.Provider>
  );
}

export default function AdminApp() {
  return (
    <ClerkAuthProvider>
      <AdminToaster position="top-right" richColors />
      <Routes>
        <Route path="sign-in/*" element={<AuthShell><SignIn routing="path" path="/admin/sign-in" /></AuthShell>} />
        <Route path="*" element={<Protected />} />
      </Routes>
    </ClerkAuthProvider>
  );
}
